import React from 'react';
import { graphql } from 'gatsby';
import CtaLink from '../Global/CtaLink';
import RichText from '../Global/RichText';
//@ts-ignore
import * as styles from './promoted-content-styleC.module.css';
import { getColor, getMediaData, getMultiChoice } from '../Global/DataUtils';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import {
    KontentStringValue,
    CTAConfiguration,
    GeneralImages,
    KontentRadioMultiChoice,
    BrandColor,
    KontentMedia,
} from '../../../types/KontentProps';

import { getImageData } from '../Global/DataUtils/gatsbyImageUtils';
import {
    optimizeKontentImageUrl,
    buildKontentSrcSet,
} from '../Global/DataUtils';

interface BulletListItem {
    content: KontentStringValue;
    useCustomIcon: KontentRadioMultiChoice<'standard_bullet' | 'custom_icon'>;
    customIcon: KontentMedia;
}

interface Props extends CTAConfiguration, GeneralImages {
    headline: KontentStringValue;
    content_panel__content_panel_color?: BrandColor;
    content_panel__content_panel_font_color?: BrandColor;
    paragraph: KontentStringValue;
    primary_image?: KontentMedia;
    bullet_list?: { value: BulletListItem[] };
    kontentItemId?: string;
}

const PromotedContentStyleC: React.FC<Props> = ({
    content_panel__content_panel_color,
    content_panel__content_panel_font_color,
    headline,
    paragraph,
    primary_image,
    cta_configuration__cta_label,
    cta_configuration__cta_location,
    cta_configuration__cta_window_action,
    cta_configuration__cta_location_anchor_point,
    bullet_list,
    kontentItemId,
}) => {
    const primaryImage = primary_image && getImageData(primary_image);
    const backgroundColor = getColor(content_panel__content_panel_color);
    const textColor = getColor(content_panel__content_panel_font_color);
    const bulletListData = bullet_list?.value.map((item: any) => {
        return {
            id: item.id,
            content: item.elements.list_content.value,
            useCustomIcon:
                item?.elements?.bullet_icons?.value[0]?.codename ===
                'custom_icon',
            customIcon: getMediaData(item.elements.custom_icon),
        };
    });
    const updatedBackgroundColor = backgroundColor ? backgroundColor : '';
    const updatedTextColor = textColor ? textColor : '';

    return (
        <div className={styles.wrapper} data-kontent-item-id={kontentItemId}>
            <div
                className={styles.container + ' ' + styles.centerFloatImage}
                style={{ backgroundColor: updatedBackgroundColor }}
            >
                <div className={styles.innerContent}>
                    <div className={styles.imageWrapper}>
                        <div
                            className={styles.modal}
                            data-kontent-element-codename="primary_image"
                        >
                            {primaryImage?.image && (
                                <img
                                    src={
                                        primaryImage.image
                                            ? optimizeKontentImageUrl(
                                                  (primaryImage.image?.url ||
                                                      primaryImage.image) as string,
                                                  {
                                                      w: 800,
                                                      fit: 'crop',
                                                      q: 80,
                                                      auto: 'format',
                                                  }
                                              )
                                            : ''
                                    }
                                    srcSet={
                                        primaryImage.image
                                            ? buildKontentSrcSet(
                                                  (primaryImage.image?.url ||
                                                      primaryImage.image) as string,
                                                  800,
                                                  {
                                                      fit: 'crop',
                                                      q: 80,
                                                      auto: 'format',
                                                  }
                                              )
                                            : ''
                                    }
                                    sizes="(max-width: 800px) 100vw, 800px"
                                    alt={primaryImage.alt}
                                    width={800}
                                    height={undefined}
                                    loading="lazy"
                                    className={styles.primaryimage}
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        objectFit: 'cover',
                                    }}
                                />
                            )}
                        </div>
                    </div>
                    <div
                        className={styles.contentBox}
                        style={{
                            backgroundColor: updatedBackgroundColor,
                            color: updatedTextColor,
                        }}
                    >
                        {headline?.value && (
                            <div className={styles.headline}>
                                <h3 data-kontent-element-codename="headline">
                                    {headline?.value}
                                </h3>
                            </div>
                        )}
                        {paragraph.value !== '<p><br></p>' && (
                            <div className={styles.paragraph}>
                                {RichText(
                                    paragraph,
                                    false,
                                    undefined,
                                    undefined,
                                    'paragraph'
                                )}
                            </div>
                        )}
                        {bulletListData && bulletListData.length > 0 && (
                            <ul className={styles.bulletList}>
                                {bulletListData.map((item: any) => (
                                    <li
                                        key={item.id}
                                        className={styles.bulletItem}
                                    >
                                        {item.useCustomIcon &&
                                        item.customIcon ? (
                                            <img
                                                src={item.customIcon?.url}
                                                alt={item.content}
                                                className={styles.customIcon}
                                                loading="lazy"
                                            />
                                        ) : (
                                            <span
                                                className={
                                                    styles.standardBullet
                                                }
                                            ></span>
                                        )}
                                        <span data-kontent-element-codename="list_content">
                                            {item.content}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {cta_configuration__cta_label?.value ? (
                            <div className={styles.ctaContainer}>
                                <CtaLink
                                    href={BuildLinkHref(
                                        cta_configuration__cta_location,
                                        cta_configuration__cta_location_anchor_point
                                    )}
                                    target={
                                        getMultiChoice(
                                            cta_configuration__cta_window_action
                                        ) === 'new_window'
                                            ? '_blank'
                                            : undefined
                                    }
                                    visualStyle="outlineLight"
                                    color={updatedTextColor}
                                >
                                    {cta_configuration__cta_label?.value}
                                </CtaLink>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};
export default PromotedContentStyleC;

export const fragmentPromotedContentStyleB = graphql`
    fragment PromotedContentStyleC on kontent_item_component___promotional_style_c {
        elements {
            headline {
                value
            }
            paragraph {
                ...kontentRichText
            }
            bullet_list {
                value {
                    ... on kontent_item_content_item___promo_c_list_item {
                        id
                        elements {
                            list_content {
                                value
                            }
                            bullet_icons {
                                value {
                                    codename
                                }
                            }
                            custom_icon {
                                value {
                                    ... on kontent_item_media___image {
                                        id
                                        elements {
                                            file {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            primary_image {
                ...kontentImage
            }
            content_panel__content_panel_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                        }
                    }
                }
            }
            content_panel__content_panel_font_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                        }
                    }
                }
            }
            cta_configuration__cta_label {
                value
            }
            cta_configuration__cta_location {
                ...kontentCtaLocation
            }
            cta_configuration__cta_location_anchor_point {
                value
            }
            cta_configuration__cta_window_action {
                value {
                    codename
                }
            }
        }
    }
`;
